import React, { useState } from "react";

import { AddFilterBaseFunc, chartAttrs, defaultStyles, IFilterKeys, IFilters, IPlotData, RemoveFilterBaseFunc } from "models/core";
import { DashboardFilterKeys } from "models/mdt";
import { PlotData } from "plotly.js";
import { Dialog } from "@mui/material";
import { addFilterFactory } from "helpers/filterHelper";
import { DashChart } from "components/common/DashChart";
import { ChartIcons } from "components/common/ChartIcons";
import { SimpleBreadcrumb } from "components/common/SimpleBreadcrumb";
import { chartDimesionsCalc } from "../core";
import watermark from "../../static/qca-black.png";
import { CreateChart } from "components/common/CreateChart";

interface IProps {
	plotData: IPlotData;
	activeFilters: IFilters<IFilterKeys>;
	addFilterFn: AddFilterBaseFunc<typeof DashboardFilterKeys>;
    removeFilterFn: RemoveFilterBaseFunc<typeof DashboardFilterKeys>;
}

/**
 * Reviews by service chart / My MDTs
 */
export function FirstTreatmentChart(props: IProps) {
	const { plotData, activeFilters, addFilterFn, removeFilterFn } = props;
	const [ chartType, setChartType ] = useState("bar");
    const [ chartOrientation, setChartOrientation ] = useState("h");
	const [ fullScreenOpen, setFullScreenState ] = useState(false);
    const { values, labels } = plotData;

	const showChart = values.length > 0;
	const filterKey = DashboardFilterKeys.FirstTreatment.key;
	const filterFn = addFilterFactory(addFilterFn, filterKey, (chartType == "pie" ? p => p.data.labels[p.pointNumber].toString() : (chartOrientation =="h" ? p => p.y.toString() : p => p.x.toString())));

	const chartDimensions = chartDimesionsCalc(50, 12, 4);
	const chartTitle = "First treatment received";
	const xtitle = (chartOrientation == "h" ? "Number of Diagnoses" : "");
	const ytitle = (chartOrientation == "h" ? "" : "Number of Diagnoses");
	const textFn = String;
	
	//Pull out the unknown index to be able to add to the bottom of the array
	//Reversed vs original labels/values allows the horizontal and vertical charts to present data in the same order 
	const indexOfUnk = plotData.labels.findIndex(x => x == "Had no treatment");

	const labelsOriginal = plotData.labels.slice();
	if (indexOfUnk !== -1)	{
		labelsOriginal.splice(indexOfUnk, 1);
	}
	
	const reversedLabels = labelsOriginal.slice().reverse();
	if (indexOfUnk !== -1)	{
		labelsOriginal.unshift(plotData.labels[indexOfUnk]);
		reversedLabels.push(plotData.labels[indexOfUnk]);
	}
		
	const valuesOriginal = plotData.values.slice();
	if (indexOfUnk !== -1)	{
		valuesOriginal.splice(indexOfUnk, 1);
	}

	const reversedValues = valuesOriginal.slice().reverse();
	if (indexOfUnk !== -1)	{
		valuesOriginal.unshift(plotData.values[indexOfUnk]);
		reversedValues.push(plotData.values[indexOfUnk]);	
	}

	const traces: Partial<PlotData>[] = [];

			switch (true) {
				case  chartType == "pie":
					traces.push({
						values: reversedValues,
						labels: reversedLabels,
						text: reversedValues.map(x => ((x / reversedValues.reduce((a, b) => a + b, 0))*100).toFixed(1) + "%"),
						texttemplate: "%{label}<br>" +
										"%{text}<br>",
						hoverinfo: "none",
						hole: 0.6,
						type: chartType,
						showlegend: false,
						direction: "clockwise",
						insidetextorientation: "horizontal",
						textposition: "outside",
						marker: {},
						sort: false,
					} as unknown as Plotly.PlotData)
				break;

				case chartOrientation == "v":
					traces.push({
						x: reversedLabels,
						y: reversedValues,
						text: reversedValues.map(textFn),
						textposition: "outside",
						textfont: {
							size: 12,
						  },
						hovertext: reversedLabels,
						hovertemplate: "%{hovertext}<br>" +
									"Diagnoses: %{y:,}<br>" +
									"<extra></extra>",
						type: chartType,
						orientation: chartOrientation,
						marker: {},
					} as unknown as Plotly.PlotData)
				break;

				default:
					traces.push({
						x: valuesOriginal,
						y: labelsOriginal,
						text: valuesOriginal.map(textFn),
						textposition: "outside",
						textfont: {
							size: 12,
						  },
						hovertext: labelsOriginal,
						hovertemplate: "%{hovertext}<br>" +
									"Diagnoses: %{x:,}<br>" +
									"<extra></extra>",
						type: chartType,
						orientation: chartOrientation,
						marker: {},
					} as unknown as Plotly.PlotData)
				break;
		}

	//Add breadcrumb
	const breadcrumb = (
		<SimpleBreadcrumb filteredValues={activeFilters[filterKey]} filterKey={filterKey} removeFilter={removeFilterFn} />
		);

	//Custom styling when a pie chart to avoid cut off labels
	const style = defaultStyles.defaultBox;
	const bx = chartAttrs(style.height);
	const styling = Object.assign({}, bx.layout)
	styling.margin = { l: 70, r: 70, b: 70, t: 70, pad: 0 };
	
	//Full screen chart
	const logo = watermark;
	const  fullScreenChart =
		<Dialog
			open={fullScreenOpen || false}
			fullWidth={true}
			maxWidth={false}
			style={{width: (window.innerWidth - 100), 
					height: ((chartDimensions.fullScreenPaperHeight*1.3) + "px"),
					marginLeft: 50,
					marginRight: 50 }}
		>
				<DashChart style={{height: (chartDimensions.fullScreenPaperHeight) + "px"}} 
							heading ={chartTitle} 
							chartKey = {chartTitle+"fullScreen"}
						> 
					<div style={{marginTop: "-70px", float: "right", display: "inline"}}>
					<ChartIcons vbar ={true} hbar ={true}
						table={true}
						scatter={true}
						pie={true}
						downloadAsCsv={true}
						downloadAsPng={true}
						isFullScreen={fullScreenOpen}
						viewAsFullScreen={true}
						tooltipInfo={false}
						chartOrientation={chartOrientation}
						chartType={chartType}
						traces = {traces}
						xLabel = {(chartOrientation=="h" ? ytitle : xtitle)} 
						yLabel = {(chartOrientation=="h" ? xtitle : ytitle)}
						title= {chartTitle}
						setChartOrientation = {setChartOrientation}
						setChartType = {setChartType}
						setFullScreen = {setFullScreenState}
						currentFilters = {activeFilters}
						/>
					<br/>
					{<img
					src={logo}
					alt="Cancer Alliance Queensland"
					width={200}
					style={{marginTop: "20px"}}
						/>}
					</div>    
					{CreateChart(chartDimensions.fullScreenChartHeight,
								 chartDimensions.fullScreenChartWidth,
								 chartDimensions.fullScreenPaperHeight,
								 chartOrientation,
								 chartType, 
								 showChart, 
								 xtitle, 
								 ytitle, 
								 labels,
								 values, 
								 traces, 
								 filterFn,
								 (chartType=="pie" ? styling : null)
								 )}
				</DashChart>
		</Dialog>


	return (
		<div className={"paper"} key={chartTitle}
        style={{
            float: "right",
            width: "100%",
			padding: "12px",
        }}
        >  
		<ChartIcons vbar ={true} hbar ={true}
			table={true}
			scatter={true}
			pie={true}
			downloadAsCsv={true}
			downloadAsPng={true}
			isFullScreen={fullScreenOpen}
			viewAsFullScreen={true}
			tooltipInfo={false}
			chartOrientation={chartOrientation}
			chartType={chartType}
			traces = {traces}
			xLabel = {(chartOrientation=="h" ? ytitle : xtitle)} 
			yLabel = {(chartOrientation=="h" ? xtitle : ytitle)}
			title= {chartTitle}
			setChartOrientation = {setChartOrientation}
            setChartType = {setChartType}
			setFullScreen = {setFullScreenState}
			currentFilters = {activeFilters}
			/>
		<DashChart chartKey={chartTitle+"chart"} heading ={chartTitle} breadcrumb={breadcrumb} style ={{height: chartDimensions.paperHeight + "px", 
																										width: chartDimensions.paperWidth}}>
			{CreateChart(chartDimensions.chartHeight,
								 chartDimensions.chartWidth,
								 chartDimensions.paperHeight,
								 chartOrientation,
								 chartType, 
								 showChart, 
								 xtitle, 
								 ytitle, 
								 labels,
								 values, 
								 traces, 
								 filterFn,
								 (chartType=="pie" ? styling : null)
								 )}
			{fullScreenChart}
		</DashChart>
		</div>
	);
}
